import React, { useEffect, useState, useCallback } from "react";
import {
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Chip, Typography, Tooltip, Tabs, Tab
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import StopIcon from "@material-ui/icons/Stop";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import AddIcon from "@material-ui/icons/Add";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";

const useStyles = makeStyles((theme) => ({
  tabs: { flexBasis: "100%", minHeight: 40, marginBottom: theme.spacing(-0.5) },
  mono: { fontVariantNumeric: "tabular-nums", color: theme.palette.text.secondary },
  titleCell: { display: "flex", alignItems: "center", gap: theme.spacing(1), cursor: "pointer", minWidth: 0 },
  root: { padding: theme.spacing(2) },
  filters: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12, alignItems: "center" },
  row: {
    display: "grid",
    gridTemplateColumns: "60px 1fr 140px 100px 140px 140px 120px 120px",
    padding: "6px 4px",
    borderBottom: "1px solid rgba(0,0,0,.07)",
    alignItems: "center",
    fontSize: 13
  },
  head: { fontWeight: 600, background: theme.palette.action.hover },
  pill: {
    display: "inline-block",
    fontSize: 10,
    padding: "2px 6px",
    borderRadius: 8,
    color: "#fff",
    fontWeight: 600
  },
  history: {
    marginTop: 8,
    maxHeight: 220,
    overflowY: "auto",
    border: "1px solid rgba(0,0,0,.08)",
    borderRadius: 4,
    padding: 6
  },
  histRow: { fontSize: 11, padding: "2px 0", borderBottom: "1px dashed rgba(0,0,0,.06)" }
}));

const PRIORITIES = [
  { value: "low", label: "Baixa", color: "#9e9e9e" },
  { value: "medium", label: "Média", color: "#2196F3" },
  { value: "high", label: "Alta", color: "#FF9800" },
  { value: "urgent", label: "Urgente", color: "#F44336" }
];
const STATUSES = [
  { value: "open", label: "Aberta" },
  { value: "in_progress", label: "Em andamento" },
  { value: "review", label: "Em revisão" },
  { value: "blocked", label: "Bloqueada" },
  { value: "done", label: "Concluída" },
  { value: "cancelled", label: "Cancelada" }
];
const TYPES = ["task", "bug", "feature", "improvement"];

const priColor = v => (PRIORITIES.find(p => p.value === v) || PRIORITIES[1]).color;
const priLabel = v => (PRIORITIES.find(p => p.value === v) || PRIORITIES[1]).label;
const statusLabel = v => (STATUSES.find(s => s.value === v) || { label: v }).label;

const Tasks = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const history = useHistory();
  const [scope, setScope] = useState(0); // 0=Avulsas, 1=Minhas, 2=Todas
  const [filterClient, setFilterClient] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [dlg, setDlg] = useState({ open: false, editing: null, form: {}, history: [] });

  const load = useCallback(async () => {
    const params = {};
    if (scope === 0) params.noProject = 1;
    if (scope === 1) params.mine = 1;
    if (filterClient) params.clientId = filterClient;
    if (filterStatus) params.status = filterStatus;
    if (filterPriority) params.priority = filterPriority;
    try {
      const { data } = await api.get("/tasks", { params });
      setTasks(data || []);
    } catch { toast.error("Erro ao carregar tarefas"); }
  }, [scope, filterClient, filterStatus, filterPriority]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then(r => setUsers(r.data?.users || [])).catch(() => {});
    api.get("/clients", { params: { pageNumber: 1, pageSize: 200 } })
      .then(r => setClients(r.data?.clients || r.data || [])).catch(() => {});
    api.get("/projects").then(r => setProjects(r.data || [])).catch(() => {});
  }, []);

  const openNew = () => setDlg({
    open: true, editing: null,
    form: { title: "", priority: "medium", status: "open", type: "task", projectId: null },
    history: []
  });

  const openEdit = async (t) => {
    try {
      const { data } = await api.get(`/tasks/${t.id}`);
      setDlg({ open: true, editing: t, form: { ...data }, history: data.history || [] });
    } catch {
      setDlg({ open: true, editing: t, form: { ...t }, history: [] });
    }
  };

  const save = async () => {
    if (!dlg.form.title) return toast.warn("Informe o título");
    try {
      if (dlg.editing) await api.put(`/tasks/${dlg.editing.id}`, dlg.form);
      else await api.post("/tasks", dlg.form);
      setDlg({ open: false, editing: null, form: {}, history: [] });
      load();
    } catch { toast.error("Erro ao salvar"); }
  };

  const remove = async (t) => {
    if (!(await confirm({ title: `Excluir a tarefa "${t.title}"?`, message: "Não dá para desfazer." }))) return;
    try { await api.delete(`/tasks/${t.id}`); load(); }
    catch { toast.error("Erro ao excluir"); }
  };

  const startTimer = async (t) => { await api.post(`/tasks/${t.id}/timer/start`); toast.success("Timer iniciado"); };
  const stopTimer = async (t) => { await api.post(`/tasks/${t.id}/timer/stop`); toast.success("Timer encerrado"); load(); };

  const columns = [
    { key: "id", label: "#", width: 64, render: t => <span className={classes.mono}>{t.id}</span> },
    {
      key: "title",
      label: "Título",
      render: t => (
        <div className={classes.titleCell} onClick={() => openEdit(t)}>
          <Typography variant="subtitle2" noWrap>{t.title}</Typography>
          {t.project?.name && <Chip size="small" label={t.project.name} />}
        </div>
      ),
    },
    { key: "status", label: "Status", render: t => statusLabel(t.status) },
    {
      key: "priority",
      label: "Prioridade",
      render: t => (
        <span className={classes.pill} style={{ background: priColor(t.priority) }}>
          {priLabel(t.priority)}
        </span>
      ),
    },
    {
      key: "client",
      label: "Cliente",
      render: t =>
        t.client?.tradeName ||
        t.client?.companyName ||
        [t.client?.firstName, t.client?.lastName].filter(Boolean).join(" ") ||
        "—",
    },
    { key: "assignee", label: "Responsável", render: t => t.assignee?.name || "—" },
    {
      key: "dueDate",
      label: "Prazo",
      render: t => <span className={classes.mono}>{t.dueDate || "—"}</span>,
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: t => (
        <>
          <Tooltip title="Iniciar timer">
            <IconButton size="small" onClick={() => startTimer(t)}><PlayArrowIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Parar timer">
            <IconButton size="small" onClick={() => stopTimer(t)}><StopIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => openEdit(t)}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" onClick={() => remove(t)}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
          {t.projectId && (
            <Tooltip title="Abrir projeto">
              <IconButton size="small" onClick={() => history.push(`/projects/${t.projectId}`)}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title="Tarefas"
        count={tasks.length}
        subtitle="Trabalho com responsável e prazo. As avulsas não pertencem a nenhum projeto."
        actions={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew}>
            Nova tarefa
          </Button>
        }
        filters={
          <>
            <Tabs
              value={scope}
              onChange={(_, v) => setScope(v)}
              indicatorColor="primary"
              textColor="primary"
              className={classes.tabs}
            >
              <Tab label="Avulsas (sem projeto)" />
              <Tab label="Minhas tarefas" />
              <Tab label="Todas" />
            </Tabs>
            <TextField select label="Cliente" style={{ minWidth: 180 }}
              value={filterClient} onChange={e => setFilterClient(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {clients.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.tradeName || c.companyName || [c.firstName, c.lastName].filter(Boolean).join(" ") || `#${c.id}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField select label="Status" style={{ minWidth: 160 }}
              value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {STATUSES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            <TextField select label="Prioridade" style={{ minWidth: 140 }}
              value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {PRIORITIES.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </TextField>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={tasks}
        emptyIcon={<AssignmentTurnedInIcon />}
        emptyTitle="Nenhuma tarefa encontrada"
        emptyDescription="Nenhuma tarefa corresponde aos filtros. Limpe os filtros ou crie a primeira tarefa."
        emptyAction={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew}>
            Nova tarefa
          </Button>
        }
      />

      <Dialog open={dlg.open} onClose={() => setDlg({ open: false, editing: null, form: {}, history: [] })} maxWidth="sm" fullWidth>
        <DialogTitle>{dlg.editing ? `Tarefa #${dlg.editing.id}` : "Nova tarefa"}</DialogTitle>
        <DialogContent>
          <TextField label="Título" fullWidth margin="dense" value={dlg.form.title || ""}
            onChange={e => setDlg({ ...dlg, form: { ...dlg.form, title: e.target.value } })} />
          <TextField label="Descrição" fullWidth multiline rows={3} margin="dense" value={dlg.form.description || ""}
            onChange={e => setDlg({ ...dlg, form: { ...dlg.form, description: e.target.value } })} />
          <div style={{ display: "flex", gap: 8 }}>
            <TextField select label="Cliente" margin="dense" style={{ flex: 1 }} value={dlg.form.clientId || ""}
              onChange={e => setDlg({ ...dlg, form: { ...dlg.form, clientId: e.target.value || null } })}>
              <MenuItem value="">—</MenuItem>
              {clients.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.tradeName || c.companyName || [c.firstName, c.lastName].filter(Boolean).join(" ") || `#${c.id}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField select label="Projeto" margin="dense" style={{ flex: 1 }} value={dlg.form.projectId || ""}
              onChange={e => setDlg({ ...dlg, form: { ...dlg.form, projectId: e.target.value || null } })}>
              <MenuItem value="">— Avulsa —</MenuItem>
              {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <TextField select label="Status" margin="dense" style={{ flex: 1 }} value={dlg.form.status || "open"}
              onChange={e => setDlg({ ...dlg, form: { ...dlg.form, status: e.target.value } })}>
              {STATUSES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            <TextField select label="Prioridade" margin="dense" style={{ flex: 1 }} value={dlg.form.priority || "medium"}
              onChange={e => setDlg({ ...dlg, form: { ...dlg.form, priority: e.target.value } })}>
              {PRIORITIES.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </TextField>
            <TextField select label="Tipo" margin="dense" style={{ flex: 1 }} value={dlg.form.type || "task"}
              onChange={e => setDlg({ ...dlg, form: { ...dlg.form, type: e.target.value } })}>
              {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </div>
          <TextField select label="Responsável" fullWidth margin="dense" value={dlg.form.assigneeId || ""}
            onChange={e => setDlg({ ...dlg, form: { ...dlg.form, assigneeId: e.target.value || null } })}>
            <MenuItem value="">—</MenuItem>
            {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
          </TextField>
          <div style={{ display: "flex", gap: 8 }}>
            <TextField label="Início" type="date" InputLabelProps={{ shrink: true }} margin="dense"
              value={dlg.form.startDate ? String(dlg.form.startDate).slice(0, 10) : ""}
              onChange={e => setDlg({ ...dlg, form: { ...dlg.form, startDate: e.target.value } })} />
            <TextField label="Prazo" type="date" InputLabelProps={{ shrink: true }} margin="dense"
              value={dlg.form.dueDate ? String(dlg.form.dueDate).slice(0, 10) : ""}
              onChange={e => setDlg({ ...dlg, form: { ...dlg.form, dueDate: e.target.value } })} />
            <TextField label="Estim. (h)" type="number" margin="dense" value={dlg.form.estimateHours || ""}
              onChange={e => setDlg({ ...dlg, form: { ...dlg.form, estimateHours: e.target.value } })} />
          </div>

          {dlg.editing && dlg.history?.length > 0 && (
            <>
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
                Histórico ({dlg.history.length})
              </Typography>
              <div className={classes.history}>
                {dlg.history.map(h => (
                  <div key={h.id} className={classes.histRow}>
                    <strong>{h.action}</strong>
                    {h.field ? ` · ${h.field}` : ""}
                    {h.fromValue ? ` · de "${h.fromValue}"` : ""}
                    {h.toValue ? ` → "${h.toValue}"` : ""}
                    <span style={{ opacity: 0.6 }}> · {h.user?.name || "sistema"} · {new Date(h.createdAt).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {dlg.editing && <Button color="secondary" onClick={() => { remove(dlg.editing); setDlg({ open: false, editing: null, form: {}, history: [] }); }}>Excluir</Button>}
          <Button onClick={() => setDlg({ open: false, editing: null, form: {}, history: [] })}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={save}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Tasks;
